import React from 'react';

/**
 * PostureBanner — top-of-page narrative banner.
 *
 * Per Design Principles §0.4 ("trust narrative on every governance
 * surface"), every Overview / Governance Center / Trust / Compliance /
 * Audit page leads with a one-sentence confidence statement. This is
 * the component that delivers it.
 *
 * Visual: a 64-px conic gauge on the left (the posture score), the
 * narrative copy in the middle, and a CTA slot on the right. A soft
 * radial accent in the success tone tints the upper-right corner so
 * the banner reads as "calm but alive" rather than flat.
 *
 * @param {Object} props
 * @param {number}   props.score — 0-100 posture score
 * @param {string}   [props.scoreLabel='Posture']
 * @param {React.ReactNode} props.title
 * @param {React.ReactNode} [props.message]
 * @param {React.ReactNode} [props.action]
 * @param {'success'|'warning'|'danger'} [props.tone] — overrides score-derived tone
 */
export function PostureBanner({ score, scoreLabel = 'Posture', title, message, action, tone }) {
  const safeScore = Math.max(0, Math.min(100, Math.round(Number(score) || 0)));

  const derivedTone =
    safeScore >= 80 ? 'success' :
    safeScore >= 65 ? 'warning' : 'danger';
  const finalTone = tone || derivedTone;

  const TONE_COLOR = {
    success: 'var(--v2-success)',
    warning: 'var(--v2-warning)',
    danger:  'var(--v2-danger)',
  };
  const TONE_SOFT = {
    success: 'var(--v2-success-soft)',
    warning: 'var(--v2-warning-soft)',
    danger:  'var(--v2-danger-soft)',
  };

  const gaugeColor = TONE_COLOR[finalTone];
  const gaugeSoft = TONE_SOFT[finalTone];
  const fillPct = Math.round(safeScore);
  const restPct = 100 - fillPct;

  return (
    <section
      role="region"
      aria-label="Platform posture"
      className={
        'bg-v2-surface border border-v2-border-subtle rounded-v2-md shadow-v2-sm ' +
        'p-5 flex items-center gap-5 relative overflow-hidden'
      }
    >
      <div
        aria-hidden="true"
        className="absolute inset-0 pointer-events-none"
        style={{
          background: `radial-gradient(ellipse at top right, ${gaugeSoft}, transparent 60%)`,
          opacity: 0.7,
        }}
      />
      <div
        aria-hidden="true"
        className="w-[64px] h-[64px] rounded-full flex items-center justify-center flex-shrink-0 relative z-10"
        style={{
          background: `conic-gradient(${gaugeColor} 0% ${fillPct}%, var(--v2-border-default) ${fillPct}% 100%)`,
        }}
      >
        <div className="w-[54px] h-[54px] bg-v2-surface rounded-full flex flex-col items-center justify-center">
          <span className="text-[18px] font-extrabold leading-none tabular-nums">{safeScore}</span>
          <span className="text-[8.5px] uppercase tracking-wider text-v2-ink-muted mt-0.5">
            {scoreLabel}
          </span>
        </div>
      </div>
      <div className="flex-1 min-w-0 relative z-10">
        <h2 className="text-[16px] font-bold tracking-tight m-0 mb-1 text-v2-ink">{title}</h2>
        {message && (
          <p className="text-[12.5px] text-v2-ink-secondary leading-relaxed m-0">{message}</p>
        )}
      </div>
      {action && (
        <div className="relative z-10 flex-shrink-0">{action}</div>
      )}
    </section>
  );
}

export default PostureBanner;
